import { useCallback, useEffect, useRef, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { usePatient } from '@hooks/usePatient';
import { createReference } from '@medplum/core';
import { cloneDeep } from 'lodash';
import BaseGraph from '../model';
import { usePedigreeContext } from './usePedigreeContext';
import { ARKON, HL7 } from '@consts/systems.consts';
import { notifications } from '@mantine/notifications';

//------------------------------------------------------------------------------
export const usePedigreeData = () => {
  const medplum = useMedplum();
  const patient = usePatient();
  const {
    baseGraph,
    setBaseGraph,
    extraData,
    setExtraData,
    refreshFlag,
    saveStateForUndo,
  } = usePedigreeContext();

  const [finishedInitialLoading, setFinishedInitialLoading] = useState(false);

  const fetchInProgress = useRef(false);

  //----------------------------------------------------------------------------
  const stringifyPedigreeGraph = (baseGraph: BaseGraph, extraData: any) => {
    return JSON.stringify({
      baseGraph,
      extraData,
    });
  };

  //----------------------------------------------------------------------------
  const parsePedigreeGraphData = (pedigreeString: string) => {
    try {
      const parsed = JSON.parse(pedigreeString);
      return parsed;
    } catch (error) {
      console.error('Failed to parse pedigree graph data:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to parse pedigree data from FHIR resource.',
        color: 'red',
      });

      return null;
    }
  };

  //----------------------------------------------------------------------------
  const loadPedigreeData = useCallback(async () => {
    if (!medplum || !patient?.id) {
      console.error('No medplum client or patient ID available');
      return;
    }

    if (fetchInProgress.current) {
      return;
    }

    fetchInProgress.current = true;

    try {
      const fmh = await medplum.searchOne('FamilyMemberHistory', {
        patient: `Patient/${patient.id}`,
        code: `${ARKON.SD.PEDIGREE.BASE}|${ARKON.SD.PEDIGREE.CODE}`,
      });

      if (!fmh) {
        return;
      }

      const pedigreeExtension = fmh.extension?.find(
        (e) => e.url === ARKON.SD.PEDIGREE.BASE
      );

      if (pedigreeExtension?.valueString) {
        const parsedData = parsePedigreeGraphData(
          pedigreeExtension.valueString
        );
        if (parsedData?.baseGraph) {
          const fetchedBaseGraph = parsedData.baseGraph;

          const clonedData = cloneDeep(fetchedBaseGraph);
          const newGraph = new BaseGraph();

          newGraph.v = clonedData.v;
          newGraph.inedges = clonedData.inedges;
          newGraph.weights = clonedData.weights;
          newGraph.type = clonedData.type;
          newGraph.properties = clonedData.properties;
          newGraph.vWidth = clonedData.vWidth;
          newGraph.maxRealVertexId = clonedData.maxRealVertexId;
          try {
            newGraph.validate();
            setBaseGraph(newGraph);
            setExtraData(parsedData.extraData || {});
            setTimeout(() => saveStateForUndo(), 0);
          } catch (error) {
            console.error('Graph validation failed:', error);
            notifications.show({
              title: 'Error',
              message: 'Failed to validate pedigree data from FHIR resource.',
              color: 'red',
            });
          }
        }
      }
    } catch (error) {
      console.error('Error loading pedigree data from FHIR:', error);
      notifications.show({
        title: 'Error',
        message: 'Error loading pedigree data from FHIR resource.',
        color: 'red',
      });
    } finally {
      fetchInProgress.current = false;
      setFinishedInitialLoading(true);
    }
  }, [medplum, patient, setBaseGraph, setExtraData, saveStateForUndo]);

  //----------------------------------------------------------------------------
  useEffect(() => {
    if (finishedInitialLoading) {
      return;
    }

    loadPedigreeData();
  }, [loadPedigreeData, finishedInitialLoading]);

  //----------------------------------------------------------------------------
  const savePedigreeData = useCallback(async () => {
    if (!medplum || !patient?.id || !baseGraph) {
      return;
    }

    try {
      let fmh = await medplum.searchOne('FamilyMemberHistory', {
        patient: `Patient/${patient.id}`,
        code: `${ARKON.SD.PEDIGREE.BASE}|${ARKON.SD.PEDIGREE.CODE}`,
      });

      const pedigreeDataString = stringifyPedigreeGraph(baseGraph, extraData);
      if (!fmh) {
        fmh = await medplum.createResource({
          resourceType: 'FamilyMemberHistory',
          status: 'partial',
          patient: createReference(patient),
          relationship: {
            coding: [
              {
                system: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.BASE,
                code: 'FAMMEMB',
                display: 'family member',
              },
            ],
          },
          extension: [
            {
              url: ARKON.SD.PEDIGREE.BASE,
              valueString: pedigreeDataString,
            },
          ],
          condition: [
            {
              code: {
                coding: [
                  {
                    system: ARKON.SD.PEDIGREE.BASE,
                    code: ARKON.SD.PEDIGREE.CODE,
                    display: 'Ainigma Pedigree Data',
                  },
                ],
              },
            },
          ],
        });
      } else {
        const updatedHistory = {
          ...fmh,
          extension: [
            {
              url: ARKON.SD.PEDIGREE.BASE,
              valueString: pedigreeDataString,
            },
          ],
        };

        await medplum.updateResource(updatedHistory);
      }
    } catch (error) {
      console.error('Failed to save pedigree data to FHIR:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to save pedigree data to FHIR resource.',
        color: 'red',
      });
    }
  }, [medplum, patient, baseGraph, extraData]);

  //----------------------------------------------------------------------------
  useEffect(() => {
    if (!finishedInitialLoading) {
      return;
    }

    if (!refreshFlag) {
      return;
    }

    savePedigreeData();
  }, [finishedInitialLoading, refreshFlag, savePedigreeData]);

  // ---------------------------------------------------------------------------
  return {
    finishedInitialLoading,
  };
};
